import { useEffect } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import { yenInc, jbaDispatchExTax } from '../../../../utils/pricing';

// Navy chapter band (same grammar as home/page.tsx NavyBand). Band = divider (#31) / no repeated heading right after a band (#33).
function NavyBand({ no, ja, en }: { no: string; ja: string; en: string }) {
  return (
    <div className="bg-[#1a2942] border-y border-[#a38e65]/25">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-5 sm:py-[26px] flex items-center gap-4 sm:gap-6">
        <img src="/images/brand/jba-logo.png" alt="" aria-hidden="true" className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0 opacity-90" />
        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl sm:text-[26px] tracking-[0.1em] shrink-0 leading-none">{no}</span>
        <h2 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-2xl lg:text-[26px] tracking-[0.08em] leading-tight">{ja}</h2>
        <span className="hidden md:block ml-auto font-['Playfair_Display',serif] italic text-[#a38e65] text-base lg:text-[19px] tracking-[0.12em]">{en}</span>
      </div>
    </div>
  );
}

// ===== Footman detail (EN) — full modernization (Implementer A scope). English only, no Japanese chars. =====
// Design base: about/overview + home. Facts from working-tree EN/JA footman + JA hub pricing. No fabrication.

export default function EnFootmanPage() {
  useEffect(() => {}, []);

  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'Butler Services', href: '/en/butler-services/' },
    { label: 'Footman' }
  ];

  const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[38px] py-[15px] text-sm tracking-[0.18em] font-['Playfair_Display',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[38px] py-[15px] text-sm tracking-[0.18em] font-['Playfair_Display',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const features = [
    { en: 'Flexible', t: 'Flexible Duration', d: 'Available from 3 hours with hourly extensions. Whether a morning only, an evening party, or full-day event support, we accommodate your schedule with ease.' },
    { en: 'Certified', t: 'Association-Certified', d: 'Only butlers certified by the Japan Butler Association serve you—professionals who completed our 130-plus-year education program and accumulated practical experience.' },
    { en: 'Customized', t: 'Fully Customizable', d: 'From party preparation and cleanup to guest reception, meal service, and beverage service, we tailor the content to your needs. Entrust all necessary support to us.' },
    { en: 'Free Selection', t: 'Free Butler Selection', d: 'Request previously engaged butlers or select from our roster at no additional charge. Find a favorite butler for ongoing service.' },
  ];

  const scenes = [
    { t: 'Home Parties & Guest Reception', d: 'When hosting or welcoming important guests, your butler handles meal service, beverages, table setting, and cleanup. As host, you focus on conversation while the occasion is elevated.' },
    { t: 'Business Events & Entertainment', d: 'For corporate entertainment and internal events, your butler manages venue setup, reception, beverage service, and coordination—dignified support that enhances your company’s prestige.' },
    { t: 'Special Occasions & Celebrations', d: 'For birthdays, anniversaries, and milestone celebrations, sharing the day with a butler creates unforgettable memories. We also assist with surprises and special atmospheres.' },
    { t: 'Temporary Housework & Filming', d: 'For post-move organizing, deep cleaning, or seasonal changeovers when you need focused help. Genuine butlers also appear on set for films and commercials with authentic bearing.' },
  ];

  const serviceGroups = [
    { t: 'Party & Event', items: ['Table setting & decoration', 'Serving food & beverages', 'Guest reception & guidance', 'Glass management & refills', 'Venue tidying & cleanup', 'Cloakroom service'] },
    { t: 'Daily Life', items: ['Meal preparation & serving', 'Cleaning & organizing', 'Laundry & ironing', 'Shopping errands', 'Pet care', 'Mail sorting'] },
    { t: 'Business', items: ['Guest reception', 'Meeting room setup & cleanup', 'Beverage service', 'Document sorting & distribution', 'VIP guest escort'] },
    { t: 'Other', items: ['Accompaniment to appointments', 'Moving & organizing luggage', 'Gift arrangement & wrapping', 'Support for special productions'] },
  ];

  const faqs = [
    ['What can be done in 3 hours?', 'Preparing, serving, and cleaning up a home party; guest reception with meal service; or intensive housework support—we build the content around your needs.'],
    ['Can I request a specific butler?', 'Yes, free of charge. Choose from our roster or name a butler you have engaged before.'],
    ['Can I extend on the day?', `Yes, if the butler’s schedule allows. The extension rate is ¥${yenInc(jbaDispatchExTax.footmanExtension)}/hour (incl. tax).`],
    ['What is the service area?', 'Primarily within and around Tokyo. Other regions are available upon consultation.'],
    ['What kind of butler will come?', 'Only experienced butlers who trained at the Japan Butler Association and passed the certification examination.'],
  ];

  const related = [
    { name: 'Butler', cad: '8 hours per day, dedicated', price: yenInc(jbaDispatchExTax.butlerBase), unit: '~ (incl. tax)', href: '/en/services/butler/' },
    { name: 'Second Steward', cad: '20 days per month, continuous', price: yenInc(jbaDispatchExTax.secondStewardMonthly), unit: '~ (incl. tax)', href: '/en/services/second-steward/' },
    { name: 'House Steward', cad: '24 hours, three-butler rotation', price: yenInc(jbaDispatchExTax.houseStewardMonthly), unit: ' (incl. tax)', href: '/en/services/house-steward/' },
  ];

  return (
    <Layout>
      <title>Footman | 3-Hour Butler Spot Service & Pricing | Japan Butler Association</title>
      <meta name="description" content={`THE BUTLER's Footman offers a professional butler from just 3 hours—ideal for home parties, guest reception, and event catering. From ¥${yenInc(jbaDispatchExTax.footmanBase)} (incl. tax, 3 hours), with free butler selection.`} />
      <link rel="canonical" href="https://butler-association.jp/en/services/footman" />

      <section className="relative bg-[#1a2942] overflow-hidden min-h-[500px] lg:min-h-[580px] flex items-center">
        <img src="/images/generated/jba-fs-en-045-services_footman.webp" alt="Footman service. A refined butler supporting guests and parties" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[700px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Plan I — Footman</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(27px,3.2vw,42px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">A butler, casually,</span>
              <span className="block">from just three hours</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              Footman is our most accessible plan, offering a professional butler from just 3 hours. Ideal for home parties, business events, guest reception, and temporary daily support, our experienced butlers deliver high-quality hospitality. Perfect for clients who wish to experience butler service for the first time.
            </p>
          </div>
        </div>
      </section>

      <Breadcrumb items={breadcrumbs} />

      <NavyBand no="I" ja="About This Service" en="Overview" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S1 Overview */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              Genuine dignity, made <span className="text-[#a38e65]">accessible</span>
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
              <p>Footman is our most accessible service, delivering high-quality hospitality by certified butlers who trained rigorously at the Japan Butler Association and accumulated practical experience.</p>
              <p>Unlike ordinary housekeeping or personal assistance, a butler brings anticipatory care from the client’s perspective, situational judgment, and dignified bearing. Footman lets you experience the refined hospitality that ordinary service staff cannot provide.</p>
              <p>For home parties and business events especially, a butler’s presence elevates the occasion. Through attentive care, smooth facilitation, and discreet yet reliable service, the host can relax and enjoy the moment.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-8 mt-10">
              {features.map((f) => (
                <div key={f.t} className="border-t border-[#e2ddd2] pt-6">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1">{f.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-2.5">{f.t}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.d}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="II" ja="Usage Scenes" en="Scenes" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S2 Scenes */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              Where Footman shines
            </h2>
            <div className="grid lg:grid-cols-[340px_1fr] gap-8 lg:gap-12 items-start">
              <img src="/images/generated/jba-space-hall-03.webp" alt="A salon hall welcoming guests" className="w-full h-64 lg:h-[360px] object-cover border border-[#a38e65]/30" />
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                {scenes.map((s) => (
                  <div key={s.t} className="border-l-2 border-[#a38e65]/40 pl-4">
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{s.t}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="III" ja="What We Provide" en="What We Do" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S3 What We Do */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              Services Footman provides
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
              We combine the services below to suit your needs. For requests beyond this list, we accommodate whenever possible—please feel free to ask.
            </p>
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-9">
              {serviceGroups.map((g) => (
                <div key={g.t} className="border-t border-[#e2ddd2] pt-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-4">{g.t}</h3>
                  <ul className="space-y-2.5">
                    {g.items.map((it) => (
                      <li key={it} className="flex items-start gap-3 font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.8]">
                        <span className="mt-2 w-1.5 h-1.5 bg-[#a38e65] shrink-0"></span>{it}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="IV" ja="Pricing" en="Fees" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S4 Pricing */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              Clear and transparent pricing
            </h2>
            <div className="grid lg:grid-cols-[300px_1fr] gap-8 lg:gap-12 items-start">
              <div className="bg-[#1a2942] p-8 text-center">
                <div className="font-['Noto_Serif_JP',serif] text-[42px] text-[#f8f6f0] leading-none">&yen;{yenInc(jbaDispatchExTax.footmanBase)}</div>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] mt-3">incl. tax &middot; 3 hours</p>
              </div>
              <div>
                <dl className="border-t-2 border-[#1a2942]">
                  {[
                    ['Base Rate', `3 hours: ¥${yenInc(jbaDispatchExTax.footmanBase)} (incl. tax)`],
                    ['Extension', `¥${yenInc(jbaDispatchExTax.footmanExtension)}/hour (incl. tax)`],
                    ['Butler Selection', 'Free'],
                    ['Transportation', 'Actual expenses'],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-6 border-b border-[#e2ddd2] py-4">
                      <dt className="font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em]">{k}</dt>
                      <dd className="font-['Noto_Serif_JP',serif] text-[17px] text-[#1a2942] text-right">{v}</dd>
                    </div>
                  ))}
                </dl>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85] mt-6">
                  <span className="text-[#8f7a56]">Payment:</span> bank transfer or credit card. Cancellation: free up to 7 days prior / 30% at 3–6 days / 100% day before or same day.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="V" ja="Frequently Asked Questions" en="FAQ" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S5 FAQ */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              Frequently asked questions
            </h2>
            <div className="max-w-3xl divide-y divide-[#e2ddd2] border-t border-[#e2ddd2]">
              {faqs.map(([q, a]) => (
                <div key={q} className="py-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2.5">Q. {q}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{a}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="VI" ja="Other Plans" en="More Options" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S6 Other Plans */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              Longer & continuous plans
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
              After experiencing a butler with Footman, move up as needed. The deeper the engagement, the more thoroughly your butler understands you.
            </p>
            <div className="grid sm:grid-cols-3 gap-6">
              {related.map((r) => (
                <a key={r.name} href={r.href} className="group block border border-[#e2ddd2] hover:border-[#a38e65] transition-colors duration-300 p-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2 group-hover:text-[#a38e65] transition-colors">{r.name}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] mb-4">{r.cad}</p>
                  <div className="font-['Noto_Serif_JP',serif] text-[22px] text-[#1a2942]">&yen;{r.price}<span className="text-sm text-[#8f7a56] ml-1">{r.unit}</span></div>
                </a>
              ))}
            </div>
            <div className="mt-8"><a href="/en/butler-services/" className={BTN08_ON_LIGHT}>Back to all services</a></div>
          </section>
        </div>
      </div>

      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S7 CTA */}
          <section>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <h2 className="jba-display font-medium text-[#f8f6f0] text-xl sm:text-2xl lg:text-[28px] leading-[1.5] mb-4">Experience professional Footman service</h2>
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                Let us know your preferred date, location, and service content. Our specialists will listen closely and propose the best plan. Estimates are free.
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                <a href="/en/contact" className={BTN08_ON_NAVY}>Contact Us</a>
                <span className="font-['Noto_Serif_JP',serif] text-[#e7e2d8] text-[15px]">Phone: 03-6824-4086 (Weekdays 10:00-18:00)</span>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
